import type { School, Status } from "../lib/types";
import { TIER_LABELS, type Tier } from "../lib/fitTier";
import { DOLLAR_LABEL, DOLLAR_MAX } from "../lib/scholarship";

const STATUS_STYLE: Record<Status, string> = {
  unranked: "bg-slate-100 text-slate-500",
  keep: "bg-green-100 text-green-800",
  maybe: "bg-amber-100 text-amber-800",
  cut: "bg-red-100 text-red-700 line-through",
};

export function StatusPill({ status }: { status: Status }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${STATUS_STYLE[status]}`}>
      {status === "unranked" ? "—" : status[0].toUpperCase() + status.slice(1)}
    </span>
  );
}

const TIER_STYLE: Record<Tier, string> = {
  dream: "bg-rose-100 text-rose-700",
  stretch: "bg-amber-100 text-amber-800",
  target: "bg-emerald-100 text-emerald-800",
  floor: "bg-sky-100 text-sky-800",
  unknown: "bg-slate-100 text-slate-400",
};

export function TierBadge({ tier }: { tier: Tier }) {
  return (
    <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${TIER_STYLE[tier]}`}>
      {TIER_LABELS[tier]}
    </span>
  );
}

/** 1–5 "$" scale for the athletic-money outcome for a rower at Katie's level.
 *  0 = no athletic aid (DIII / Ivy); service academies are 5 (full ride for everyone).
 *  Hover for the basis. */
export function ScholarshipDollars({
  school,
  showLabel = false,
}: {
  school: School;
  showLabel?: boolean;
}) {
  const n = school.scholarship_dollars ?? 0;
  const label = school.scholarship_label || DOLLAR_LABEL[n];
  const title = school.scholarship_basis || label;
  if (n < 1) {
    return (
      <span className="inline-flex items-center gap-1.5" title={title}>
        <span className="text-xs font-medium text-slate-400">none</span>
        {showLabel && <span className="text-xs text-slate-500">{DOLLAR_LABEL[0]}</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span className="font-semibold tracking-tight tabular-nums text-emerald-700" aria-label={`${n} of ${DOLLAR_MAX}`}>
        {"$".repeat(n)}
      </span>
      {showLabel && <span className="text-xs text-slate-500">{label}</span>}
    </span>
  );
}

/** 0–5 hearts: first-year retention vs. what the school's acceptance rate predicts. */
export function StudentLoveHearts({
  school,
  showLabel = false,
}: {
  school: School;
  showLabel?: boolean;
}) {
  const n = school.student_love;
  if (n == null) return <span className="text-slate-300">—</span>;
  const pp = school.retention_vs_expected == null ? null : Math.round(school.retention_vs_expected * 100);
  const title =
    school.student_love_basis ||
    (pp == null ? "" : `Retention ${pp >= 0 ? "+" : ""}${pp}pp vs. expected for its selectivity`);
  return (
    <span className="inline-flex items-center gap-1.5" title={title} aria-label={`${n} of 5 hearts`}>
      <span className="tracking-tight text-rose-500">
        {n === 0 ? <span className="text-rose-300">♡</span> : "♥".repeat(n)}
      </span>
      {showLabel && pp != null && (
        <span className="text-xs text-slate-500">
          {pp >= 0 ? "+" : ""}
          {pp}pp vs. expected
        </span>
      )}
    </span>
  );
}

const FLAG_LABEL: Record<string, string> = {
  no_cmax_data: "No cMax data — needs a manual deep-dive",
  cmax_estimated: "cMax is a hand estimate (row2k couldn't model this program) — treat as rough",
  conference_unverified: "Conference unverified (aggregator-sourced)",
  coach_email_generic: "Coach email is a generic/recruiting address",
  coach_unverified: "Coach not yet confirmed on the live page — verify before emailing",
};

export function Flags({ flags }: { flags: string[] }) {
  if (!flags?.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span
          key={f}
          className="rounded bg-yellow-100 px-1 py-0.5 text-[10px] text-yellow-800"
          title={FLAG_LABEL[f] || f}
        >
          {FLAG_LABEL[f] || f}
        </span>
      ))}
    </span>
  );
}
